import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Search, Plus, Filter, Download, 
  ArrowUpRight, Eye, Edit, Trash2, 
  DollarSign, Clock, CheckCircle, FileText 
} from "lucide-react";
import { motion } from "framer-motion";

import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";

// Modals
import AddInvoiceModal from "../../components/operationsAssociate/AddInvoiceModal";
import EditInvoiceModal from "../../components/operationsAssociate/EditInvoiceModal";
import ViewInvoiceModal from "../../components/operationsAssociate/ViewInvoiceModal";

const API_URL = "https://ims.holdthechild.net";

const VendorInvoicePage = () => {
  // --- UI State ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // --- Data State ---
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Modal States ---
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // --- Search & Filter ---
  const [searchVendor, setSearchVendor] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/vendor-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.data || response.data || []);
    } catch (err) {
      setError("Invoicing synchronization offline.");
      toast.error("Registry Sync Failed ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm("CONFIRM SYSTEM DELETION?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/vendor-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Record Purged ✅");
      fetchInvoices();
    } catch (err) {
      toast.error("Deletion Blocked ❌");
    }
  };

  // Logic: Filters
  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch = inv.po?.vendorName?.toLowerCase().includes(searchVendor.toLowerCase()) || 
                          inv.invoiceNumber?.toLowerCase().includes(searchVendor.toLowerCase());
    const matchesVendor = !filterVendor || inv.po?.vendorName === filterVendor;
    const matchesStatus = !filterStatus || inv.status === filterStatus;
    return matchesSearch && matchesVendor && matchesStatus;
  });

  const vendorList = [...new Set(invoices.map((inv) => inv.po?.vendorName).filter(Boolean))];

  // --- UI Components ---
  const InvoiceMatrix = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[
        { label: "Total Receivables", val: `$${filteredInvoices.reduce((a, b) => a + (b.amount || 0), 0).toLocaleString()}`, color: "text-blue-600", icon: DollarSign },
        { label: "Pending Approval", val: filteredInvoices.filter(i => i.status === "Pending").length, color: "text-orange-600", icon: Clock },
        { label: "Settled Invoices", val: filteredInvoices.filter(i => i.status === "Paid").length, color: "text-emerald-600", icon: CheckCircle },
        { label: "Registry Volume", val: filteredInvoices.length, color: "text-purple-600", icon: FileText },
      ].map((stat, i) => (
        <div key={i} className="bg-white p-5 rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1">{stat.label}</p>
            <p className={`text-xl font-black ${stat.color}`}>{stat.val}</p>
          </div>
          <div className="bg-gray-50 p-2 rounded-xl">
            <stat.icon size={16} className={stat.color} />
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-gray-50 font-black text-orange-600 tracking-widest">
      SYNCHRONIZING INVOICE LEDGER...
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed} 
        setCollapsed={setSidebarCollapsed} 
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto space-y-8">
            
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  Invoice <span className="text-orange-600">Ledger</span>
                </h1>
                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1 italic">Financial Command Module</p>
              </div>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-gray-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-lg shadow-gray-200 flex items-center gap-2"
              >
                <Plus size={16}/> Record New Invoice
              </button>
            </div>

            <InvoiceMatrix />

            {/* Registry Filters */}
            <section className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[250px] relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input 
                  type="text" 
                  placeholder="SEARCH VENDOR OR INVOICE #..." 
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-[10px] font-black uppercase tracking-widest focus:ring-2 focus:ring-orange-500 border-none"
                  value={searchVendor}
                  onChange={(e) => setSearchVendor(e.target.value)}
                />
              </div>
              <select 
                className="bg-gray-50 px-4 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-none"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Paid">Paid</option>
                <option value="Rejected">Rejected</option>
              </select>
            </section>

            {/* Invoice Table Registry */}
            <section className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Document Intel</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Vendor Context</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Valuation</th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                      <th className="px-8 py-5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice._id} className="group hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-5">
                          <div>
                            <p className="text-sm font-black text-gray-800 uppercase leading-none">{invoice.invoiceNumber}</p>
                            <p className="text-[9px] font-black text-orange-600 mt-1 uppercase tracking-widest italic">PO: {invoice.po?.poNumber || "N/A"}</p>
                          </div>
                        </td>
                        <td className="px-8 py-5 text-xs font-black text-gray-600 uppercase">
                          {invoice.po?.vendorName || "UNKNOWN VENDOR"}
                        </td>
                        <td className="px-8 py-5">
                          <p className="text-xs font-black text-gray-700">${invoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Due: {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</p>
                        </td>
                        <td className="px-8 py-5">
                          <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                            invoice.status === 'Paid' ? 'bg-emerald-50 text-emerald-600' : 
                            invoice.status === 'Pending' ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-600'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-8 py-5 text-right">
                          <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button onClick={() => { setSelectedInvoice(invoice); setIsViewModalOpen(true); }} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg">
                              <Eye size={14} />
                            </button>
                            <button onClick={() => { setSelectedInvoice(invoice); setIsEditModalOpen(true); }} className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => handleDeleteInvoice(invoice._id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* --- Modals --- */}
      {isAddModalOpen && <AddInvoiceModal closeModal={() => { setIsAddModalOpen(false); fetchInvoices(); }} />}
      {isEditModalOpen && selectedInvoice && (
        <EditInvoiceModal invoice={selectedInvoice} closeModal={() => { setIsEditModalOpen(false); setSelectedInvoice(null); fetchInvoices(); }} />
      )}
      {isViewModalOpen && selectedInvoice && (
        <ViewInvoiceModal invoice={selectedInvoice} closeModal={() => { setIsViewModalOpen(false); setSelectedInvoice(null); }} />
      )}
    </div>
  );
};

export default VendorInvoicePage;